import type { DisciplinaryCase } from "../../types";
import type { FafAuditEvent } from "../../types/faf-audit.types";
import { bucketMatchCases, type MatchBucketMeta } from "./matchBuckets";

/**
 * Contrôle rapport FAF : rows of pv_report_check (incidents and excessive
 * bookings read from the referee report / match sheet, already compared with
 * the league's sanctions, plus the PV's own duplicate sanctions) and the FAF
 * card audit, grouped exactly like the PDF buckets. Pure — the hook in
 * hooks/usePVReportCheck.ts feeds it.
 */

export type ReportCheckKind =
    | "incident"
    | "excessive_bookings"
    | "duplicate_sanction"
    | "absorbed_yellow"
    | "no_sheet"
    | "empty_report"
    | "not_analysed";
export type ReportCheckDuplicateKind = "duplicate_sanction" | "absorbed_yellow";
export type ReportCheckStatus = "recorded" | "dismissed" | "recorded_outside_pv" | "missing";
export type ReportCheckStatusKind = "no_sheet" | "empty_report" | "not_analysed";

/** How a sanction got into the PV. */
export type ReportCheckSanctionOrigin = "faf_card" | "match_entry" | "pv" | "carry_over";

/**
 * One sanction of a duplicate group, or a team sanction that could cover an
 * uncovered incident, as pv_report_check returns it.
 */
export interface ReportCheckSanction {
    id: string;
    case_id: string | null;
    type: string;
    subject_type: "player" | "staff" | "team";
    subject_name: string | null;
    subject_name_ar: string | null;
    infraction_title_fr: string | null;
    infraction_title_ar: string | null;
    /** Cover candidates only; absent before 20261028143713. */
    reason_id?: string | null;
    reason_fr: string | null;
    reason_ar: string | null;
    fine_amount: number | null;
    suspension_matches: number | null;
    decision_date: string | null;
    created_at: string | null;
    origin: ReportCheckSanctionOrigin;
}

export const isDuplicateKind = (kind: ReportCheckKind): kind is ReportCheckDuplicateKind =>
    kind === "duplicate_sanction" || kind === "absorbed_yellow";

/**
 * « Couvert par cette sanction » : the sanctions of the incident's team whose
 * motif could be taught the incident's family. Alerts, hints and ignored
 * incidents only — a covered one needs nothing.
 */
export function coverCandidates(item: ReportCheckItem): ReportCheckSanction[] {
    if (item.source !== "report" || item.row.kind !== "incident" || !item.row.family) return [];
    if (item.severity !== "alert" && item.severity !== "hint" && item.severity !== "dismissed") return [];
    return item.row.sanctions.filter((s) => !!s.reason_id);
}

export interface ReportCheckRpcRow {
    match_id: string;
    case_id: string | null;
    faf_match_id: string | null;
    kind: ReportCheckKind;
    family: string | null;
    suggested_infraction_id: string | null;
    suggested_infraction_code: string | null;
    team_id: string | null;
    team_name: string | null;
    team_name_ar: string | null;
    side: "home" | "away" | null;
    minute: number | null;
    details: string | null;
    event_count: number | null;
    status: ReportCheckStatus | null;
    sanction_ids: string[] | null;
    fingerprint: string | null;
    /** Duplicate kinds, and an uncovered incident's cover candidates (since 20261028143713). */
    sanctions?: ReportCheckSanction[] | null;
}

export interface ReportCheckRow {
    matchId: string;
    caseId: string | null;
    fafMatchId: string | null;
    kind: ReportCheckKind;
    family: string | null;
    suggestedInfractionId: string | null;
    suggestedInfractionCode: string | null;
    teamId: string | null;
    teamName: string | null;
    teamNameAr: string | null;
    side: "home" | "away" | null;
    minute: number | null;
    details: string | null;
    eventCount: number;
    status: ReportCheckStatus | null;
    sanctionIds: string[];
    fingerprint: string | null;
    /**
     * Duplicate kinds: the group's sanctions, oldest first. Uncovered incident:
     * the team's sanctions of the match that have a motif (cover candidates).
     */
    sanctions: ReportCheckSanction[];
}

export interface ReportCheckDismissal {
    matchId: string;
    fingerprint: string;
    note: string | null;
    dismissedAt: string;
}

/** duplicate: the PV holds the same sanction twice (or a yellow its expulsion already covers). */
export type ReportCheckSeverity = "duplicate" | "alert" | "attach" | "hint" | "done" | "dismissed";

interface ItemBase {
    key: string;
    severity: ReportCheckSeverity;
    fingerprint: string;
    dismissal: ReportCheckDismissal | null;
}

/**
 * The FAF sheet's cards of the team behind an excessive-bookings finding.
 * FAF records a second-yellow expulsion as a yellow plus a red, so `players`
 * (distinct booked or sent-off people, staff included — what pv_report_check
 * counts) can be lower than yellow + red.
 */
export interface ReportCheckBookings {
    yellow: number;
    red: number;
    players: number;
}

export type ReportCheckItem =
    | (ItemBase & { source: "report"; row: ReportCheckRow; bookings?: ReportCheckBookings | null })
    | (ItemBase & { source: "card"; event: FafAuditEvent });

export interface ReportCheckMatch {
    caseId: string;
    matchId: string;
    fafMatchId: string | null;
    statusKinds: ReportCheckStatusKind[];
    /** duplicate, alert, attach, hint — sorted. */
    open: ReportCheckItem[];
    done: ReportCheckItem[];
    dismissed: ReportCheckItem[];
}

export interface ReportCheckBucket {
    key: string;
    leagueGroupId: string | null;
    leagueGroupName: string | null;
    leagueGroupNameAr: string | null;
    round: number | null;
    matches: ReportCheckMatch[];
}

export interface ReportCheckTotals {
    duplicate: number;
    alert: number;
    attach: number;
    hint: number;
    done: number;
    dismissed: number;
    noSheet: number;
    emptyReport: number;
    notAnalysed: number;
}

export interface ReportCheckView {
    buckets: ReportCheckBucket[];
    /** Alerts only — the match-card badge. */
    alertCountByCaseId: Record<string, number>;
    totals: ReportCheckTotals;
    /** Something the league can act on: a duplicate, an alert or a sanction to attach. */
    hasOpenItems: boolean;
    /** Matches with at least one open item (duplicate, alert, attach or hint). */
    openMatchCount: number;
}

export type ReportCheckMeta = MatchBucketMeta & {
    leagueGroupName?: string | null;
    /** The case's sanctions (MatchCaseMeta.sanctions); absent = unknown, a matched card counts as in the PV. */
    sanctions?: ReadonlyArray<{ id: string }>;
};

export interface BuildReportCheckViewInput {
    cases: DisciplinaryCase[];
    matchMeta: Record<string, ReportCheckMeta | undefined>;
    rows: ReportCheckRow[];
    cardAudit: Array<{ matchId: string; fafMatchId: string | null; fafEvents: FafAuditEvent[] }>;
    dismissals: ReportCheckDismissal[];
}

export function mapReportCheckRow(r: ReportCheckRpcRow): ReportCheckRow {
    return {
        matchId: r.match_id,
        caseId: r.case_id,
        fafMatchId: r.faf_match_id,
        kind: r.kind,
        family: r.family,
        suggestedInfractionId: r.suggested_infraction_id,
        suggestedInfractionCode: r.suggested_infraction_code,
        teamId: r.team_id,
        teamName: r.team_name,
        teamNameAr: r.team_name_ar,
        side: r.side,
        minute: r.minute,
        details: r.details,
        eventCount: r.event_count ?? 1,
        status: r.status,
        sanctionIds: r.sanction_ids ?? [],
        fingerprint: r.fingerprint,
        sanctions: r.sanctions ?? [],
    };
}

/** Stable across re-scrapes: licence, else the normalised name, never the FAF event id. */
export function cardFingerprint(e: Pick<FafAuditEvent, "eventType" | "licenseNumber" | "playerName" | "minute">): string {
    const colour = e.eventType === "red_card" ? "red" : "yellow";
    const licence = e.licenseNumber?.trim();
    const name = (e.playerName ?? "").replace(/\s+/g, " ").trim().toUpperCase();
    return `card:${colour}:${licence || name || "?"}:${e.minute ?? "?"}`;
}

export function incidentSeverity(row: ReportCheckRow): ReportCheckSeverity {
    if (isDuplicateKind(row.kind)) return row.status === "dismissed" ? "dismissed" : "duplicate";
    switch (row.status) {
        case "recorded":
            return "done";
        case "dismissed":
            return "dismissed";
        case "recorded_outside_pv":
            return "attach";
        default:
            return row.teamId ? "alert" : "hint";
    }
}

/**
 * `inPv`: the matched sanction sits in one of the PV's cases. A card whose
 * sanction exists but was never attached is « Hors PV », like an incident
 * recorded outside the PV: the PDF would miss it.
 */
export function cardSeverity(e: FafAuditEvent, dismissal: ReportCheckDismissal | null, inPv = true): ReportCheckSeverity {
    if (e.status === "ok" && inPv) return "done";
    if (dismissal) return "dismissed";
    if (e.status === "ok") return "attach";
    return e.status === "missing" ? "alert" : "hint";
}

const cardPlayerKey = (e: Pick<FafAuditEvent, "licenseNumber" | "playerName" | "fafEventId">): string =>
    e.licenseNumber?.trim() || (e.playerName ?? "").replace(/\s+/g, " ").trim().toUpperCase() || e.fafEventId;

/** Cards of one side of the sheet; null without the audit (cards not loaded). */
export function bookingsOf(events: FafAuditEvent[] | undefined, side: "home" | "away" | null): ReportCheckBookings | null {
    if (!events || !side) return null;
    const mine = events.filter((e) => e.isHomeTeam === (side === "home"));
    if (mine.length === 0) return null;
    return {
        yellow: mine.filter((e) => e.eventType === "yellow_card").length,
        red: mine.filter((e) => e.eventType === "red_card").length,
        players: new Set(mine.map(cardPlayerKey)).size,
    };
}

const SEVERITY_RANK: Record<ReportCheckSeverity, number> = { duplicate: 0, alert: 1, attach: 2, hint: 3, done: 4, dismissed: 5 };
const STATUS_KINDS: ReportCheckStatusKind[] = ["no_sheet", "empty_report", "not_analysed"];

const itemMinute = (i: ReportCheckItem): number | null => (i.source === "report" ? i.row.minute : i.event.minute);

const byOpenOrder = (a: ReportCheckItem, b: ReportCheckItem): number => {
    const s = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
    if (s !== 0) return s;
    const am = itemMinute(a);
    const bm = itemMinute(b);
    if (am === bm) return 0;
    if (am === null) return 1;
    if (bm === null) return -1;
    return am - bm;
};

export function buildReportCheckView({ cases, matchMeta, rows, cardAudit, dismissals }: BuildReportCheckViewInput): ReportCheckView {
    const totals: ReportCheckTotals = { duplicate: 0, alert: 0, attach: 0, hint: 0, done: 0, dismissed: 0, noSheet: 0, emptyReport: 0, notAnalysed: 0 };
    const alertCountByCaseId: Record<string, number> = {};

    const dismissalOf = new Map(dismissals.map((d) => [`${d.matchId}|${d.fingerprint}`, d]));
    const rowsByMatch = new Map<string, ReportCheckRow[]>();
    for (const r of rows) {
        const list = rowsByMatch.get(r.matchId) ?? [];
        list.push(r);
        rowsByMatch.set(r.matchId, list);
    }
    const auditByMatch = new Map(cardAudit.map((a) => [a.matchId, a]));
    const metas = cases.map((c) => matchMeta[c.id]).filter((m): m is ReportCheckMeta => !!m);
    const pvSanctionIds = metas.some((m) => m.sanctions)
        ? new Set(metas.flatMap((m) => m.sanctions ?? []).map((s) => s.id))
        : null;

    const matchCases = cases.filter((c) => (c.caseType ?? "match_sanction") === "match_sanction" && !!c.matchId);
    const seen = new Set<string>();
    const buckets: ReportCheckBucket[] = [];
    let openMatchCount = 0;

    for (const bucket of bucketMatchCases(matchCases, matchMeta)) {
        const matches: ReportCheckMatch[] = [];
        let groupName: string | null = null;
        for (const c of bucket.cases) {
            const matchId = c.matchId as string;
            if (seen.has(matchId)) continue;
            seen.add(matchId);
            groupName = groupName ?? matchMeta[c.id]?.leagueGroupName ?? null;

            const matchRows = rowsByMatch.get(matchId) ?? [];
            const audit = auditByMatch.get(matchId);
            const items: ReportCheckItem[] = [];
            const statusKinds: ReportCheckStatusKind[] = [];

            for (const row of matchRows) {
                if ((STATUS_KINDS as string[]).includes(row.kind)) {
                    const kind = row.kind as ReportCheckStatusKind;
                    if (!statusKinds.includes(kind)) statusKinds.push(kind);
                    continue;
                }
                if (!row.fingerprint) continue;
                items.push({
                    source: "report",
                    key: `${matchId}:${row.fingerprint}`,
                    severity: incidentSeverity(row),
                    fingerprint: row.fingerprint,
                    dismissal: dismissalOf.get(`${matchId}|${row.fingerprint}`) ?? null,
                    row,
                    bookings: row.kind === "excessive_bookings" ? bookingsOf(audit?.fafEvents, row.side) : null,
                });
            }
            for (const event of audit?.fafEvents ?? []) {
                const fingerprint = cardFingerprint(event);
                const dismissal = dismissalOf.get(`${matchId}|${fingerprint}`) ?? null;
                items.push({
                    source: "card",
                    key: `${matchId}:${fingerprint}:${event.fafEventId}`,
                    severity: cardSeverity(
                        event,
                        dismissal,
                        !pvSanctionIds || !event.matchedSanctionId || pvSanctionIds.has(event.matchedSanctionId),
                    ),
                    fingerprint,
                    dismissal,
                    event,
                });
            }

            statusKinds.sort((a, b) => STATUS_KINDS.indexOf(a) - STATUS_KINDS.indexOf(b));
            const open = items.filter((i) => SEVERITY_RANK[i.severity] <= SEVERITY_RANK.hint).sort(byOpenOrder);
            const done = items.filter((i) => i.severity === "done");
            const dismissed = items.filter((i) => i.severity === "dismissed");

            for (const i of items) totals[i.severity]++;
            if (statusKinds.includes("no_sheet")) totals.noSheet++;
            if (statusKinds.includes("empty_report")) totals.emptyReport++;
            if (statusKinds.includes("not_analysed")) totals.notAnalysed++;
            const alerts = open.filter((i) => i.severity === "alert").length;
            if (alerts > 0) alertCountByCaseId[c.id] = alerts;
            if (open.length > 0) openMatchCount++;

            matches.push({
                caseId: c.id,
                matchId,
                fafMatchId: matchRows.find((r) => r.fafMatchId)?.fafMatchId ?? audit?.fafMatchId ?? null,
                statusKinds,
                open,
                done,
                dismissed,
            });
        }
        if (matches.length === 0) continue;
        buckets.push({
            key: bucket.key,
            leagueGroupId: bucket.leagueGroupId,
            leagueGroupName: groupName,
            leagueGroupNameAr: bucket.leagueGroupNameAr,
            round: bucket.round,
            matches,
        });
    }

    return { buckets, alertCountByCaseId, totals, hasOpenItems: totals.duplicate + totals.alert + totals.attach > 0, openMatchCount };
}

/** What the toolbar's counters filter on: one severity, or every open item (« à contrôler »). */
export type ReportCheckFilter = "open" | "duplicate" | "alert" | "attach" | "hint";

/** Matches for « open », findings for a severity. */
export function reportCheckFilterCount(view: ReportCheckView, filter: ReportCheckFilter): number {
    return filter === "open" ? view.openMatchCount : view.totals[filter];
}

/**
 * The view restricted to one filter: matches without such an item are
 * dropped (then empty buckets), and each kept match shows only those items.
 * Totals and badge counts stay those of the full view.
 */
export function filterReportCheckView(view: ReportCheckView, filter: ReportCheckFilter): ReportCheckView {
    const buckets: ReportCheckBucket[] = [];
    for (const bucket of view.buckets) {
        const matches: ReportCheckMatch[] = [];
        for (const match of bucket.matches) {
            const open = filter === "open" ? match.open : match.open.filter((i) => i.severity === filter);
            if (open.length > 0) matches.push({ ...match, open, done: [], dismissed: [] });
        }
        if (matches.length > 0) buckets.push({ ...bucket, matches });
    }
    return { ...view, buckets };
}
